import { VpTabs, VpTabsList, VpTabsTrigger } from '@vtmn-play/react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useParams, useSearchParams } from 'react-router'
import { ProductsGallery } from '@/components/ProductsGallery'
import { ProductGalleryOnboarding } from '@/components/ProductsGallery/ProductGalleryOnboarding'
import { ProductsList } from '@/components/ProductsList'
import { CountriesProvider } from '@/contexts/countries/CountriesProvider'
import { CzListSetsProvider } from '@/contexts/cz-list-sets/CzListSetsProvider'
import { ANALYTICS_EVENTS, consumePendingJourneyChannel, trackAnalyticsEvent } from '@/services/analytics'
import { BackToTopButton } from './BackToTopButton'
import { ProductsPageHeader } from './ProductsPageHeader'
import './products-page.scss'

export function ProductsPage() {
  const { t } = useTranslation()
  const location = useLocation()
  const [, setSearchParams] = useSearchParams()
  const { season, universeId, departmentId, subdepartmentId } = useParams()
  const tabs = ['gallery', 'products'] as const
  const [activeTab, setActiveTab] = useState<typeof tabs[number]>('gallery')

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const selectedFamilyIds = searchParams.getAll('families')
    const viewMode = searchParams.get('viewMode') ?? 'sm'
    const sourceChannel = consumePendingJourneyChannel() ?? 'direct'

    trackAnalyticsEvent(ANALYTICS_EVENTS.PRODUCTS_PAGE_OPENED, {
      source_channel: sourceChannel,
      season,
      universe_id: universeId ? Number(universeId) : undefined,
      department_id: departmentId ? Number(departmentId) : undefined,
      subdepartment_id: subdepartmentId ? Number(subdepartmentId) : undefined,
      selected_families_count: selectedFamilyIds.length,
      view_mode: viewMode ?? undefined,
      path: location.pathname,
    })
  }, [departmentId, location.pathname, location.search, season, subdepartmentId, universeId])

  const handleTabClick = (tab: typeof tabs[number]) => {
    setActiveTab(tab)

    if (tab === 'products') {
      setSearchParams((previous) => {
        if (!previous.has('homologation_countries'))
          return previous

        const next = new URLSearchParams(previous)
        next.delete('homologation_countries')
        return next
      })

      trackAnalyticsEvent(ANALYTICS_EVENTS.PRODUCTS_TAB_CLICKED, {
        tab,
        season,
        universe_id: universeId ? Number(universeId) : undefined,
        department_id: departmentId ? Number(departmentId) : undefined,
        subdepartment_id: subdepartmentId ? Number(subdepartmentId) : undefined,
      })
    }
  }

  const renderTabPanel = () => {
    switch (activeTab) {
      case 'gallery':
        return <ProductsGallery />
      case 'products':
        return <ProductsList />
      default:
        return null
    }
  }

  return (
    <CountriesProvider>
      <CzListSetsProvider>
        <ProductGalleryOnboarding>
          <section className="products-page" data-testid="products_page">
            <ProductsPageHeader isProductsTabActive={activeTab === 'products'} />
            <div data-testid="products_container" className="products-container">
              <div data-testid="products_tabs" className="products-tabs">
                <VpTabs value={activeTab} onChange={(value: typeof tabs[number]) => handleTabClick(value)}>
                  <VpTabsList aria-label={t('aria-label.product-views-tablist')}>
                    {tabs.map(tab => (
                      <VpTabsTrigger
                        id={`product-${tab}-tab-products`}
                        data-testid={`tab-${tab}`}
                        key={tab}
                        value={tab}
                        aria-label={t(`products-page.tabs.${tab}`)}
                      >
                        {t(`products-page.tabs.${tab}`)}
                      </VpTabsTrigger>
                    ))}
                  </VpTabsList>
                </VpTabs>
                <div className="tabs-panels">
                  <div id={`tabpanel-${activeTab}`} role="tabpanel" className="tab-panel">
                    {renderTabPanel()}
                  </div>
                </div>
              </div>
            </div>
            <BackToTopButton />
          </section>
        </ProductGalleryOnboarding>
      </CzListSetsProvider>
    </CountriesProvider>
  )
}
